// Frontend wrapper for /api/artworks. Three round-trips per session:
//
//   open       — POST /api/artworks            -> {artworkId, putUrl, ...}
//   autosave   — PUT  /api/artworks/:id        -> re-signed URLs
//   complete   — POST /api/artworks/:id/complete
//
// putR2(url, blob) is a stand-alone helper because R2 PUT is the uploader
// path the page calls a lot — keeping it here means the practice page never
// touches R2 directly except through a typed function.

import {
  artworkAutosaveResponseSchema,
  artworkCompleteResponseSchema,
  artworkGetResponseSchema,
  artworkOpenResponseSchema,
  type ArtworkAutosaveResponse,
  type ArtworkCompleteResponse,
  type ArtworkGetResponse,
  type ArtworkOpenResponse,
  type PackId,
} from '@skena/schema'

interface ApiSuccess<T> {
  data: T
}
interface ApiError {
  error: { code: string; message: string; details?: unknown }
}
type ApiResult<T> = ApiSuccess<T> | ApiError

export class ArtworksApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
  ) {
    super(message)
  }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new ArtworksApiError(err.code, err.message, res.status)
  }
  return json.data
}

async function putJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new ArtworksApiError(err.code, err.message, res.status)
  }
  return json.data
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' })
  const json = (await res.json().catch(() => ({}))) as ApiResult<T>
  if (!res.ok || 'error' in json) {
    const err = 'error' in json ? json.error : { code: 'INTERNAL', message: 'unexpected error' }
    throw new ArtworksApiError(err.code, err.message, res.status)
  }
  return json.data
}

export const artworksApi = {
  async open(packId: PackId, itemId: string): Promise<ArtworkOpenResponse> {
    const raw = await postJson<unknown>('/api/artworks', { packId, itemId })
    return artworkOpenResponseSchema.parse(raw)
  },
  async autosave(
    artworkId: string,
    fillActionCount?: number,
  ): Promise<ArtworkAutosaveResponse> {
    const raw = await putJson<unknown>(`/api/artworks/${artworkId}`, {
      fillActionCount,
    })
    return artworkAutosaveResponseSchema.parse(raw)
  },
  async complete(
    artworkId: string,
    fillActionCount: number,
  ): Promise<ArtworkCompleteResponse> {
    const raw = await postJson<unknown>(`/api/artworks/${artworkId}/complete`, {
      fillActionCount,
    })
    return artworkCompleteResponseSchema.parse(raw)
  },
  async get(artworkId: string): Promise<ArtworkGetResponse> {
    const raw = await getJson<unknown>(`/api/artworks/${artworkId}`)
    return artworkGetResponseSchema.parse(raw)
  },
}

/**
 * Direct R2 PUT. Cookies aren't sent (cross-origin), and we don't need the
 * response body — just status.
 */
export async function putR2(url: string, blob: Blob): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': blob.type || 'application/octet-stream' },
  })
  if (!res.ok) {
    throw new ArtworksApiError(
      'R2_UPLOAD_FAILED',
      `R2 PUT failed: ${res.status} ${res.statusText}`,
      res.status,
    )
  }
}
